import {
    Dropzone as MantineDropzone,
    DropzoneProps as MantineDropzoneProps,
    FileWithPath,
} from '@mantine/dropzone';
import { JSX, useCallback, useRef } from 'react';

import dropzoneStyles from './Dropzone.module.css';
import { Group, Stack, Text } from '@mantine/core';
import { Button } from '../button/Button';
import { generateAcceptedMimeTypesString, formatFileSize } from './utils';
import { StatusNegativeIcon } from '../../icons/StatusNegativeIcon';

export type DropzoneProps = MantineDropzoneProps & {
    /**
     * Label text shown above dropzone
     */
    label?: string;
    /**
     * Description text shown below label and  above dropzone
     */
    description?: string;
    /**
     * Whether the file is required. Displays an asterisk next to the label
     */
    required?: boolean;
    /**
     * The accepted file to shown below the dropzone. Currently only supports a single file
     * shown at a time
     */
    acceptedFile?: FileWithPath;
    /**
     * Mime types of the files that dropzone can accepts. By default, dropzone accepts all file types.
     */
    accept?: string[];
    /**
     * Determines whether a loading UI should be displayed in the dropzone, false by default
     */
    loading?: boolean;
    /**
     * Props for the loading UI
     */
    loadingProps?: {
        /**
         * Label text shown in the loading UI
         */
        label?: string;
    };
    /**
     * Error message shown below the dropzone. If the error is a boolean, it will be shown as a default error message
     */
    error?: string | boolean;
};

export const Dropzone: typeof MantineDropzone & {
    (props: DropzoneProps): JSX.Element;
} = Object.assign(
    ({
        label,
        description,
        accept,
        required,
        loading,
        loadingProps,
        disabled,
        error,
        acceptedFile,
        inputProps,
        ...props
    }: DropzoneProps) => {
        const openRef = useRef<() => void>(null);

        const handleChangeFileClick = useCallback(() => {
            openRef.current?.();
        }, []);

        return (
            <Stack className={dropzoneStyles.root} gap={4}>
                <Stack gap={0}>
                    <Group gap={2}>
                        {!!label && <Text className="dart-Dropzone-label">{label}</Text>}
                        {!!required && <Text className="dart-Dropzone-asterisk">*</Text>}
                    </Group>
                    {!!description && <Text className="dart-Dropzone-description">{description}</Text>}
                </Stack>
                <MantineDropzone
                    className={dropzoneStyles.dropzoneRoot}
                    accept={accept}
                    disabled={loading || disabled}
                    openRef={openRef}
                    inputProps={{
                        ...inputProps,
                        'aria-label': inputProps?.['aria-label'] || label || 'Upload file here',
                    }}
                    {...props}
                >
                    {!loading && (
                        <Stack align="center" flex={1} gap={8}>
                            <Group justify="center" align="center" gap={4}>
                                <Button appearance="outline" intent="neutral">
                                    Upload
                                </Button>
                                <Text>or drag it here</Text>
                            </Group>

                            {accept && (
                                <Text className="dart-Dropzone-acceptedText">
                                    {generateAcceptedMimeTypesString(accept)}
                                </Text>
                            )}
                        </Stack>
                    )}

                    {loading && (
                        <Text className="dart-Dropzone-loadingLabel">
                            {loadingProps?.label ?? 'Uploading file(s)...'}
                        </Text>
                    )}
                </MantineDropzone>

                {acceptedFile && !loading && (
                    <Stack
                        gap={4}
                        className="dart-Dropzone-acceptedFileRoot"
                        style={{
                            flexGrow: 0,
                            flexBasis: '100%',
                            flexWrap: 'nowrap',
                        }}
                    >
                        <Text className="dart-Dropzone-acceptedFileLabel">Uploaded File</Text>
                        <Group
                            gap={8}
                            style={{
                                flexGrow: 0,
                                flexShrink: 1,
                                flexWrap: 'nowrap',
                            }}
                        >
                            <Text className="dart-Dropzone-acceptedFileName">{acceptedFile.name}</Text>
                            <Text className="dart-Dropzone-acceptedFileSize">
                                {formatFileSize(acceptedFile.size)}
                            </Text>
                            <Button
                                appearance="outline"
                                intent="neutral"
                                size="xs"
                                onClick={handleChangeFileClick}
                            >
                                Change File
                            </Button>
                        </Group>
                    </Stack>
                )}

                {error && (
                    <Group gap={4}>
                        <StatusNegativeIcon className="dart-Dropzone-errorIcon" />
                        <Text className="dart-Dropzone-errorText">
                            {typeof error === 'string' ? error : error === true ? 'Invalid file type' : ''}
                        </Text>
                    </Group>
                )}
            </Stack>
        );
    },
    MantineDropzone
);
